/**
 * Route-level error boundary.
 * Catches React errors in the routed page tree, renders a recoverable
 * fallback (instead of a blank page), and lets the user navigate away.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      // Surface in dev console — production gets Sentry once that's wired.
      console.error("[ErrorBoundary]", error, info.componentStack);
    }
  }

  reset = (): void => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          maxWidth: 560,
          margin: "80px auto",
          padding: 32,
          textAlign: "center",
        }}
      >
        <p
          style={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            color: "var(--accent-red)",
            marginBottom: 12,
          }}
        >
          Something broke on this page
        </p>
        <h1
          style={{
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: "-0.3px",
            margin: "0 0 12px",
          }}
        >
          We couldn't render this view.
        </h1>
        <p
          style={{
            color: "var(--body)",
            lineHeight: 1.6,
            marginBottom: 20,
            fontSize: 14,
          }}
        >
          The error has been logged. You can try again, navigate to the board, or sign
          out and back in if the problem persists.
        </p>
        <details
          style={{
            textAlign: "left",
            background: "var(--surface-1)",
            border: "1px solid var(--hairline)",
            borderRadius: 4,
            padding: 12,
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--body)",
            marginBottom: 16,
          }}
        >
          <summary style={{ cursor: "pointer", color: "var(--mute)" }}>
            Technical details
          </summary>
          <pre
            style={{
              marginTop: 8,
              overflow: "auto",
              maxHeight: 240,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {error.name}: {error.message}
            {import.meta.env.DEV && error.stack ? "\n" + error.stack : ""}
          </pre>
        </details>
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          <button
            type="button"
            onClick={this.reset}
            style={{
              background: "var(--ink)",
              color: "var(--on-primary)",
              border: "none",
              borderRadius: 4,
              padding: "8px 14px",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
          <a
            href="/board/APO"
            style={{
              background: "var(--canvas)",
              color: "var(--ink)",
              border: "1px solid var(--hairline)",
              borderRadius: 4,
              padding: "8px 14px",
              fontSize: 13,
              textDecoration: "none",
            }}
          >
            Back to board
          </a>
        </div>
      </div>
    );
  }
}
